import React, {useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import PatientLookupDialog from 'src/components-new/data/PatientLookupDialog'
import DoctorsLookupDialog from 'src/components-new/data/DoctorsLookupDialog'
import DrugsLookupDialog from 'src/components-new/data/DrugsLookupDialog'
import MedicalCentersLookupDialog from 'src/components-new/data/MedicalCentersLookupDialog'
import {useAppSelector} from 'src/hooks'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

export interface ExportRequestValues {
  startDate: string
  endDate: string
  exportType: string
  patientCode?: string
  doctor?: string
  drug?: string
  medicalClinic?: string
  patient?: Record<string, any>
}

interface ExportDialogNewProps {
  open: boolean
  exportType: string
  exportTypeLabel: string
  onClose: () => void
  onSave: (values: ExportRequestValues) => void | Promise<void>
}

interface LookupFieldProps {
  label: string
  name: string
  onOpen: () => void
  onClear: () => void
}

// Same "search / clear" lookup control as RequestSampleDialogNew.tsx's Paziente block —
// used here for both the CLIENT group's Medico lookup and the DOCTOR group's Paziente
// lookup, matching the legacy ExportDialog.tsx's `.user`/`.user-buttons` fields.
function LookupField({label, name, onOpen, onClear}: LookupFieldProps): JSX.Element {
  return (
    <Box>
      <Typography
        sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}
        variant="caption"
      >
        {label}
      </Typography>
      {name ? (
        <Box sx={{mt: 0.5}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              py: 0.75,
              borderRadius: '8px',
              backgroundColor: NEUTRAL_SCALE[50],
              border: `1px solid ${NEUTRAL_SCALE[200]}`,
            }}
          >
            <Typography variant="body2">{name}</Typography>
            <ActionIconButton onClick={onClear} tooltip="Rimuovi" variant="close" />
          </Box>
        </Box>
      ) : (
        <Box sx={{mt: 0.5}}>
          <Button onClick={onOpen} startIcon={<SearchIcon fontSize="small" />} variant="ghost">
            {`Aggiungi ${label.toLowerCase()}`}
          </Button>
        </Box>
      )}
    </Box>
  )
}

/**
 * New-style equivalent of src/components/templates/ExportDialog/ExportDialog.tsx. Collects
 * the form data here and delegates the actual apiClient.postExport call / toast / list
 * refresh to the parent's onSave, same division of responsibility as
 * biologicalSamples/RequestSampleDialogNew.tsx. The export type itself is chosen by the
 * caller BEFORE opening this dialog (one action per enabled `exportConfigurations.exportTypes`
 * entry, see ExportsListNew.tsx), matching the legacy page's headerActions, which each set
 * `exportId` and only then open ExportDialog — the type is never picked inside the dialog.
 */
export default function ExportDialogNew({
  open,
  exportType,
  exportTypeLabel,
  onClose,
  onSave,
}: ExportDialogNewProps): JSX.Element {
  const userGroup = useAppSelector(state => state.userReducer.data?.userGroup)
  const scope = useAppSelector(state => state.pspConfigReducer.data.scope)
  const isClient = Boolean(userGroup?.toUpperCase().includes('CLIENT'))
  const isDoctor = Boolean(userGroup?.toUpperCase().includes('DOCTOR'))

  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const [startDateError, setStartDateError] = useState(false)
  const [endDateError, setEndDateError] = useState(false)

  const [patientCode, setPatientCode] = useState('')

  const [currentDoctor, setCurrentDoctor] = useState<Record<string, any>>()
  const [doctorLookupOpen, setDoctorLookupOpen] = useState(false)

  const [currentPatient, setCurrentPatient] = useState<Record<string, any>>()
  const [patientLookupOpen, setPatientLookupOpen] = useState(false)

  const [currentDrug, setCurrentDrug] = useState<Record<string, any>>()
  const [drugLookupOpen, setDrugLookupOpen] = useState(false)

  const [currentMedicalClinic, setCurrentMedicalClinic] = useState<Record<string, any>>()
  const [medicalClinicLookupOpen, setMedicalClinicLookupOpen] = useState(false)

  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) {
      setStartDate(null)
      setEndDate(null)
      setStartDateError(false)
      setEndDateError(false)
      setPatientCode('')
      setCurrentDoctor(undefined)
      setDoctorLookupOpen(false)
      setCurrentPatient(undefined)
      setPatientLookupOpen(false)
      setCurrentDrug(undefined)
      setDrugLookupOpen(false)
      setCurrentMedicalClinic(undefined)
      setMedicalClinicLookupOpen(false)
      setSaving(false)
    }
  }, [open])

  const handleSubmit = async () => {
    const missingStartDate = !startDate
    const missingEndDate = !endDate
    setStartDateError(missingStartDate)
    setEndDateError(missingEndDate)
    if (missingStartDate || missingEndDate || saving) return

    setSaving(true)
    try {
      await onSave({
        startDate,
        endDate,
        exportType,
        ...(isClient ? {
          patientCode: patientCode || undefined,
          doctor: currentDoctor?._id,
          drug: currentDrug?._id,
          medicalClinic: currentMedicalClinic?._id,
        } : {}),
        ...(isDoctor ? {
          patient: currentPatient,
          drug: currentDrug?._id,
        } : {}),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <AppDialog
        actions={
          <>
            <Button disabled={saving} onClick={onClose} variant="secondary">Annulla</Button>
            <Button loading={saving} onClick={handleSubmit} variant="primary">Salva</Button>
          </>
        }
        maxWidth="md"
        onClose={onClose}
        open={open}
        title={exportTypeLabel}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
            gap: 2,
            pt: 1,
          }}
        >
          <DatePickerNew
            error={startDateError}
            fullWidth
            helperText={startDateError ? 'Campo obbligatorio' : undefined}
            label="Data inizio report"
            onChange={setStartDate}
            required
            value={startDate}
          />
          <DatePickerNew
            error={endDateError}
            fullWidth
            helperText={endDateError ? 'Campo obbligatorio' : undefined}
            label="Data fine report"
            onChange={setEndDate}
            required
            value={endDate}
          />

          {isClient && (
            <>
              <TextFieldNew
                fullWidth
                label={scope === 'tutor' ? 'DM Paziente' : 'Codice paziente'}
                onChange={setPatientCode}
                value={patientCode}
              />
              <LookupField
                label="Medico"
                name={currentDoctor ? `${currentDoctor.firstName ?? ''} ${currentDoctor.lastName ?? ''}`.trim() : ''}
                onClear={() => setCurrentDoctor(undefined)}
                onOpen={() => setDoctorLookupOpen(true)}
              />
              <LookupField
                label="Farmaco"
                name={currentDrug?.name ?? ''}
                onClear={() => setCurrentDrug(undefined)}
                onOpen={() => setDrugLookupOpen(true)}
              />
              <LookupField
                label="Centro medico"
                name={currentMedicalClinic?.name ?? ''}
                onClear={() => setCurrentMedicalClinic(undefined)}
                onOpen={() => setMedicalClinicLookupOpen(true)}
              />
            </>
          )}

          {isDoctor && (
            <>
              <LookupField
                label="Paziente"
                name={currentPatient ? `${currentPatient.firstName ?? ''} ${currentPatient.lastName ?? ''}`.trim() : ''}
                onClear={() => setCurrentPatient(undefined)}
                onOpen={() => setPatientLookupOpen(true)}
              />
              <LookupField
                label="Farmaco"
                name={currentDrug?.name ?? ''}
                onClear={() => setCurrentDrug(undefined)}
                onOpen={() => setDrugLookupOpen(true)}
              />
            </>
          )}
        </Box>
      </AppDialog>

      {doctorLookupOpen && (
        <DoctorsLookupDialog
          customFilters={[{property: 'code', value: '!test'}]}
          onClose={() => setDoctorLookupOpen(false)}
          onRowClick={(row) => {
            setCurrentDoctor(row)
            setDoctorLookupOpen(false)
          }}
        />
      )}

      {patientLookupOpen && (
        <PatientLookupDialog
          onClose={() => setPatientLookupOpen(false)}
          onRowClick={(row) => {
            setCurrentPatient(row)
            setPatientLookupOpen(false)
          }}
        />
      )}

      {drugLookupOpen && (
        <DrugsLookupDialog
          onClose={() => setDrugLookupOpen(false)}
          onRowClick={(row) => {
            setCurrentDrug(row)
            setDrugLookupOpen(false)
          }}
        />
      )}

      {medicalClinicLookupOpen && (
        <MedicalCentersLookupDialog
          onClose={() => setMedicalClinicLookupOpen(false)}
          onRowClick={(row) => {
            setCurrentMedicalClinic(row)
            setMedicalClinicLookupOpen(false)
          }}
        />
      )}
    </>
  )
}
